import { Injectable } from 'framework/di';

import { AppService } from '../app/app.service';

/**
 * Cart selectors managing theme-specific cart icon configurations
 *
 * Provides cart icon selectors and update actions for 30+ Shopify themes including:
 * - Dawn, Debut, Sense, Craft, Refresh, Studio, Taste, Ride, Crave
 * - Prestige, Empire, Impulse, Motion, Flex, Flow, Venue, Warehouse
 * - Turbo, Testament, Venture, Symetry, Superstore, Icon, Ella
 * - BlockShop, ColorBlock, Broadcast, Focal, Expanse
 * - And more custom themes
 *
 * Technical Support teams can:
 * - Add custom theme configurations via window.boostWidgetIntegrationConfig
 * - Override theme detection logic
 * - Customize cart count update behavior
 *
 * @example
 * ```javascript
 * // Add custom theme configuration
 * window.boostWidgetIntegrationConfig = {
 *   updateCartIconActionForThemes: {
 *     customTheme: {
 *       selector: '.my-cart-icon',
 *       action: (count) => {
 *         document.querySelector('.cart-count').textContent = count;
 *       }
 *     }
 *   }
 * };
 *
 * // Extend CartSelectors for custom behavior
 * window.boostWidgetIntegration.extend('CartSelectors', (CartSelectors) => {
 *   return class CustomCartSelectors extends CartSelectors {
 *     getCartSelectorAndUpdateAction() {
 *       // Try custom logic first
 *       const customConfig = this.getCustomThemeConfig();
 *       if (customConfig) return customConfig;
 *
 *       // Fallback to default
 *       return super.getCartSelectorAndUpdateAction();
 *     }
 *
 *     getCustomThemeConfig() {
 *       const shopTheme = window.Shopify?.theme?.name;
 *       return this.updateCartIconActionForThemes[shopTheme];
 *     }
 *   };
 * });
 * ```
 */
@Injectable
export class CartSelectors {
  /**
   * Theme-specific cart icon configurations
   *
   * Maps theme names to their cart icon selectors and update actions.
   * Technical Support teams can extend this configuration via window.boostWidgetIntegrationConfig.
   *
   * @protected - Allows extension by subclasses for custom theme configurations
   */
  protected updateCartIconActionForThemes = {
    blockShop: {
      selector: '.header--cart',
      action: (cartItemCount: number) => {
        const cartIconHeaderWrapper = document.querySelector('.cart--external--icon');
        const cartCount = document.querySelector('.cart--external--total-items');

        if (!cartIconHeaderWrapper || !cartCount) return;

        cartIconHeaderWrapper.setAttribute('data-item-count', cartItemCount.toString());
        this.safelySetCartCount(cartCount, cartItemCount);
      },
    },
    colorBlock: {
      theme: 'abc',
      selector: '.header__icon--cart',
      action: (cartItemCount: number) => {
        const cartIconHeaderWrapper = document.querySelector('.header__icon--cart');

        if (!cartIconHeaderWrapper) return;

        document.querySelector('.cart-count-bubble')?.remove();

        if (cartItemCount === 0) return;

        const cartCountEl = this.createCartCountBubble(cartItemCount);

        cartIconHeaderWrapper.append(cartCountEl);
      },
    },
    craft: {
      selector: '.header__icon--cart',
      action: (cartItemCount: number) => {
        const cartIconHeaderWrapper = document.querySelector('.header__icon--cart');

        if (!cartIconHeaderWrapper) return;

        document.querySelector('.cart-count-bubble')?.remove();

        if (cartItemCount === 0) return;

        const cartCountEl = this.createCartCountBubble(cartItemCount);

        cartIconHeaderWrapper.append(cartCountEl);
      },
    },
    crave: {
      selector: '.header__icon--cart',
      action: (cartItemCount: number) => {
        const cartIconHeaderWrapper = document.querySelector('.header__icon--cart');

        if (!cartIconHeaderWrapper) return;

        document.querySelector('.cart-count-bubble')?.remove();

        if (cartItemCount === 0) return;

        const cartCountEl = this.createCartCountBubble(cartItemCount);

        cartIconHeaderWrapper.append(cartCountEl);
      },
    },
    dawn: {
      selector: '.header__icon--cart',
      action: (cartItemCount: number) => {
        const cartIconHeaderWrapper = document.querySelector('.header__icon--cart');

        if (!cartIconHeaderWrapper) return;

        document.querySelector('.cart-count-bubble')?.remove();

        if (cartItemCount === 0) return;

        const cartCountEl = this.createCartCountBubble(cartItemCount);

        cartIconHeaderWrapper.append(cartCountEl);
      },
    },
    debut: {
      selector: '.site-header__icon.site-header__cart',
      action: (cartItemCount: number) => {
        const cartIconHeaderWrapper = document.querySelector('.site-header__cart');

        if (!cartIconHeaderWrapper) return;

        document.getElementById('CartCount')?.remove();

        const cartCountEl = this.createDebutCartCount(cartItemCount);

        cartIconHeaderWrapper.append(cartCountEl);
      },
    },
    default: {
      selector: '.header__icon--cart',
      action: (cartItemCount: number) => {
        const cartIconHeaderWrapper = document.querySelector('.header__icon--cart');

        if (!cartIconHeaderWrapper) return;

        document.querySelector('.cart-count-bubble')?.remove();

        if (cartItemCount === 0) return;

        const cartCountEl = this.createCartCountBubble(cartItemCount);

        cartIconHeaderWrapper.append(cartCountEl);
      },
    },
    ella: {
      selector: '.cart-icon a',
      action: (cartItemCount: number) => {
        const cartCountEls = document.querySelectorAll('.cartCount');

        cartCountEls.forEach((item) => this.safelySetCartCount(item, cartItemCount));
      },
    },
    empire: {
      selector: '.site-header-cart--button',
      action: (cartItemCount: number) => {
        const cartCountEl = document.querySelector('.site-header-cart--count');

        if (!cartCountEl) return;

        cartCountEl.className = `site-header-cart--count ${cartItemCount > 0 ? 'visible' : ''}`;
        cartCountEl.setAttribute('data-header-cart-count', `${cartItemCount || ''}`);
      },
    },
    flex: {
      selector: '.header-cart .header__link',
      action: (cartItemCount: number) => {
        const cartCountEls = document.querySelectorAll('.header-cart__count');
        const cartIconEls = document.querySelectorAll('.header-cart');

        cartIconEls.forEach(
          (item) =>
            (item.className = `header-cart action-area__link ${
              cartItemCount > 0 ? 'has-cart-count' : ''
            }`)
        );
        cartCountEls.forEach((item) => this.safelySetCartCount(item, cartItemCount));
      },
    },
    flow: {
      selector: '.site-header__cart-toggle',
      action: (cartItemCount: number) => {
        const cartCountEl = document.querySelector('.cart-item-count-header');

        if (!cartCountEl) return;

        const cartCountClassName = `cart-item-count-header cart-item-count-header--has-label cart-item-count-header--quantity ${
          cartItemCount > 0 ? '' : 'hide'
        }`;

        cartCountEl.className = cartCountClassName;
        this.safelySetCartCount(cartCountEl, cartItemCount);
      },
    },
    icon: {
      selector: '.slideout__trigger--open a',
      action: (cartItemCount: number) => {
        const cartCountEls = document.querySelectorAll('.js-cart-count');

        cartCountEls.forEach((item) => this.safelySetCartCount(item, cartItemCount));
      },
    },
    impulse: {
      selector: '.js-drawer-open-cart',
      action: (cartItemCount: number) => {
        const cartCountEl = document.querySelector('.cart-link__bubble');

        if (!cartCountEl) return;

        const cartCountClassName = `cart-link__bubble ${
          cartItemCount > 0 ? 'cart-link__bubble--visible' : ''
        }`;

        cartCountEl.className = cartCountClassName;
      },
    },
    motion: {
      selector: '.js-drawer-open-cart',
      action: (cartItemCount: number) => {
        const cartCountEl = document.querySelector('.cart-link__bubble');

        if (!cartCountEl) return;

        const cartCountClassName = `cart-link__bubble ${
          cartItemCount > 0 ? 'cart-link__bubble--visible' : ''
        }`;

        cartCountEl.className = cartCountClassName;
      },
    },
    prestige: {
      selector: '.Header__Wrapper [href*="cart"]',
      action: (cartItemCount: number) => {
        const cartDotEl = document.querySelector('.Header__CartDot');

        if (cartDotEl) {
          const cartCountClassName = `Header__CartDot ${cartItemCount > 0 ? 'is-visible' : ''}`;

          cartDotEl.className = cartCountClassName;
        }

        const cartCountEl = document.querySelector('.Header__CartCount');

        if (cartCountEl) {
          this.safelySetCartCount(cartCountEl, cartItemCount);
        }
      },
    },
    refresh: {
      selector: '.header__icon--cart',
      action: (cartItemCount: number) => {
        const cartIconHeaderWrapper = document.querySelector('.header__icon--cart');

        if (!cartIconHeaderWrapper) return;

        document.querySelector('.cart-count-bubble')?.remove();

        if (cartItemCount === 0) return;

        const cartCountEl = this.createCartCountBubble(cartItemCount);

        cartIconHeaderWrapper.append(cartCountEl);
      },
    },
    ride: {
      selector: '.header__icon--cart',
      action: (cartItemCount: number) => {
        const cartIconHeaderWrapper = document.querySelector('.header__icon--cart');

        if (!cartIconHeaderWrapper) return;

        document.querySelector('.cart-count-bubble')?.remove();

        if (cartItemCount === 0) return;

        const cartCountEl = this.createCartCountBubble(cartItemCount);

        cartIconHeaderWrapper.append(cartCountEl);
      },
    },
    sense: {
      selector: '.header__icon--cart',
      action: (cartItemCount: number) => {
        const cartIconHeaderWrapper = document.querySelector('.header__icon--cart');

        if (!cartIconHeaderWrapper) return;

        document.querySelector('.cart-count-bubble')?.remove();

        if (cartItemCount === 0) return;

        const cartCountEl = this.createCartCountBubble(cartItemCount);

        cartIconHeaderWrapper.append(cartCountEl);
      },
    },
    studio: {
      selector: '.header__icon--cart',
      action: (cartItemCount: number) => {
        const cartIconHeaderWrapper = document.querySelector('.header__icon--cart');

        if (!cartIconHeaderWrapper) return;

        document.querySelector('.cart-count-bubble')?.remove();

        if (cartItemCount === 0) return;

        const cartCountEl = this.createCartCountBubble(cartItemCount);

        cartIconHeaderWrapper.append(cartCountEl);
      },
    },
    superstore: {
      selector: '.site-header-cart-button',
      action: (cartItemCount: number) => {
        const cartCountEl = document.querySelector('.site-header-cart--count');

        if (!cartCountEl) return;

        cartCountEl.className = `site-header-cart--count ${cartItemCount > 0 ? 'visible' : ''}`;
        cartCountEl.setAttribute('data-header-cart-count', `${cartItemCount || ''}`);
      },
    },
    symetry: {
      selector: 'a.cart-link',
      action: (cartItemCount: number) => {
        const cartIconHeaderWrapper = document.querySelector('.cart-link__icon');

        if (!cartIconHeaderWrapper) return;

        document.querySelector('.cart-link__count')?.remove();

        if (cartItemCount === 0) return;

        const cartCountEl = document.createElement('span');
        cartCountEl.className = 'cart-link__count';
        this.safelySetCartCount(cartCountEl, cartItemCount);

        cartIconHeaderWrapper.append(cartCountEl);
      },
    },
    taste: {
      selector: '.header__icon--cart',
      action: (cartItemCount: number) => {
        const cartIconHeaderWrapper = document.querySelector('.header__icon--cart');

        if (!cartIconHeaderWrapper) return;

        document.querySelector('.cart-count-bubble')?.remove();

        if (cartItemCount === 0) return;

        const cartCountEl = this.createCartCountBubble(cartItemCount);

        cartIconHeaderWrapper.append(cartCountEl);
      },
    },
    testament: {
      selector: '.js-mini-cart-trigger',
      action: (cartItemCount: number) => {
        const cartCountEls = document.querySelectorAll('.js-cart-count');

        cartCountEls.forEach((item) => this.safelySetCartCount(item, cartItemCount));
      },
    },
    turbo: {
      selector: '.header .icon-cart',
      action: (cartItemCount: number) => {
        const cartCountEls = document.querySelectorAll('.cart_count');

        cartCountEls.forEach((item) => this.safelySetCartCount(item, cartItemCount));
      },
    },
    venture: {
      selector: '.site-header__link.site-header__cart',
      action: (cartItemCount: number) => {
        const cartCountEl = document.querySelector('.site-header__cart-indicator');

        if (!cartCountEl) return;

        const cartCountClassName = `site-header__cart-indicator ${cartItemCount > 0 ? '' : 'hide'}`;

        cartCountEl.className = cartCountClassName;
      },
    },
    venue: {
      selector: '.header-trigger__link--cart',
      action: (cartItemCount: number) => {
        const cartIconHeaderWrapper = document.querySelector('.header-trigger__link--cart');

        if (!cartIconHeaderWrapper) return;

        document.querySelector('.cart-count-bubble')?.remove();

        if (cartItemCount === 0) return;

        const cartCountEl = this.createCartCountBubble(cartItemCount);

        cartIconHeaderWrapper.append(cartCountEl);
      },
    },
    warehouse: {
      selector: '.header__cart-toggle',
      action: (cartItemCount: number) => {
        const cartCountEl = document.querySelector('.header__cart-count');

        if (!cartCountEl) return;

        this.safelySetCartCount(cartCountEl, cartItemCount);
      },
    },
    expanse: {
      selector: '#HeaderCartTrigger',
      action: (cartItemCount: number) => {
        const cartBubbleEl = document.querySelector('.cart-link__bubble');
        const cartCountEl = document.querySelector('.cart-link__bubble-num');

        if (!cartBubbleEl || !cartCountEl) return;

        const cartBubbleClassName = `cart-link__bubble ${
          cartItemCount > 0 ? 'cart-link__bubble--visible' : ''
        }`;
        cartBubbleEl.className = cartBubbleClassName;

        this.safelySetCartCount(cartCountEl, cartItemCount);
      },
    },
    focal: {
      selector: '.header__linklist-item [href*="cart"]',
      action: (cartItemCount: number) => {
        const cartCountEl = document.querySelector('.header__linklist-item .header__cart-count');

        if (!cartCountEl) return;

        this.safelySetCartCount(cartCountEl, cartItemCount);
      },
    },
    broadcast: {
      selector: '.navlink--cart',
      action: (cartItemCount: number) => {
        const mobileCartCountEl = document.querySelector(
          '.header__mobile__button .header__cart__status'
        );
        const desktopCartCountEl = document.querySelector(
          '.header__desktop__button .header__cart__status'
        );
        if (!desktopCartCountEl && !mobileCartCountEl) return;
        if (mobileCartCountEl) {
          this.safelySetCartCount(mobileCartCountEl, cartItemCount);
          mobileCartCountEl.setAttribute('data-cart-count', cartItemCount.toString());
        }
        if (desktopCartCountEl) {
          this.safelySetCartCount(desktopCartCountEl, cartItemCount);
          desktopCartCountEl.setAttribute('data-cart-count', cartItemCount.toString());
        }
      },
    },
    horizon: {
      selector: '.header-actions__cart-icon',
      action: (cartItemCount: number) => {
        const cartBubbleEl = document.querySelector<HTMLElement>('.cart-bubble');
        const cartCountEl = document.querySelector<HTMLElement>('.cart-bubble__text-count');

        if (!cartBubbleEl || !cartCountEl) return;

        const hasItems = cartItemCount > 0;

        cartBubbleEl.className = `cart-bubble${hasItems ? '' : ' visually-hidden'}`;

        cartCountEl.className = `cart-bubble__text-count${hasItems ? '' : ' hidden'}`;

        this.safelySetCartCount(cartCountEl, cartItemCount);
      },
    },
  };

  constructor(protected appService: AppService) {
    this.exposeConfig();
  }
  /**
   * Safely create cart count bubble element with proper text content (XSS-safe)
   * @param cartItemCount - Cart item count to display
   * @param className - CSS class for the bubble element
   * @returns HTMLDivElement with safe text content
   * @protected
   */
  protected createCartCountBubble(
    cartItemCount: number,
    className = 'cart-count-bubble'
  ): HTMLDivElement {
    const cartCountEl = document.createElement('div');
    cartCountEl.className = className;

    // Create aria-hidden span with safe textContent (not innerHTML)
    const countSpan = document.createElement('span');
    countSpan.setAttribute('aria-hidden', 'true');
    countSpan.textContent = cartItemCount.toString();

    // Create visually-hidden span with safe textContent (not innerHTML)
    const visuallyHiddenSpan = document.createElement('span');
    visuallyHiddenSpan.className = 'visually-hidden';
    visuallyHiddenSpan.textContent = `${cartItemCount} ${cartItemCount === 1 ? 'item' : 'items'}`;

    cartCountEl.appendChild(countSpan);
    cartCountEl.appendChild(document.createTextNode('\n'));
    cartCountEl.appendChild(visuallyHiddenSpan);

    return cartCountEl;
  }

  /**
   * Safely update element text content (XSS-safe)
   * Uses textContent instead of innerHTML to prevent XSS vulnerabilities
   * @param element - DOM element to update
   * @param count - Count value to set
   * @protected
   */
  protected safelySetCartCount(element: Element, count: number): void {
    element.textContent = count.toString();
  }

  /**
   * Safely create Debut theme cart count element (XSS-safe)
   * @param cartItemCount - Cart item count to display
   * @returns HTMLDivElement with safe text content
   * @protected
   */
  protected createDebutCartCount(cartItemCount: number): HTMLDivElement {
    const cartCountEl = document.createElement('div');
    cartCountEl.id = 'CartCount';
    cartCountEl.className = `site-header__cart-count ${
      cartItemCount > 0 ? '' : 'hide'
    } critical-hidden`;

    // Create count span with safe textContent (not innerHTML)
    const countSpan = document.createElement('span');
    countSpan.setAttribute('data-cart-count', '');
    countSpan.textContent = cartItemCount.toString();

    // Create fallback text span with safe textContent (not innerHTML)
    const fallbackSpan = document.createElement('span');
    fallbackSpan.className = 'icon__fallback-text medium-up--hide';
    fallbackSpan.textContent = 'items';

    cartCountEl.appendChild(countSpan);
    cartCountEl.appendChild(document.createTextNode('\n '));
    cartCountEl.appendChild(fallbackSpan);

    return cartCountEl;
  }

  /**
   * Expose cart icon configuration to window for runtime customization
   * Protected to allow Technical Support teams to customize config exposure
   *
   * Override to:
   * - Add custom configuration merging logic
   * - Validate theme configurations
   * - Add logging or analytics
   *
   * @example
   * ```typescript
   * protected exposeConfig(): void {
   *   super.exposeConfig();
   *   console.log('Cart config exposed:', window.boostWidgetIntegrationConfig);
   * }
   * ```
   */
  protected exposeConfig(): void {
    const TAEApp = this.appService.TAEApp;
    TAEApp.updateConfig({
      customization: {
        cart: {
          updateCartIconActionForThemes: this.updateCartIconActionForThemes,
        },
      },
    });
  }

  /**
   * Get cart selector configuration for current theme
   *
   * Detects which Shopify theme is active by checking for theme-specific selectors
   * Returns the first matching theme configuration found
   *
   * @returns Theme cart configuration or undefined if no match found
   * @example
   * ```javascript
   * const config = cartSelectors.getCartSelectorAndUpdateAction();
   * if (config) {
   *   config.action(5); // Update cart count to 5
   * }
   * ```
   */
  getCartSelectorAndUpdateAction():
    | { selector: string; action: (cartItemCount: number) => void; theme?: string }
    | undefined {
    // Check external config first (window.boostWidgetIntegrationConfig) for custom overrides
    const externalThemes = (window as any).boostWidgetIntegrationConfig
      ?.updateCartIconActionForThemes;
    if (externalThemes) {
      const externalMatch = Object.values(
        externalThemes as Record<
          string,
          { selector: string; action: (cartItemCount: number) => void }
        >
      ).find((item) => document.querySelector(item.selector));
      if (externalMatch) return externalMatch;
    }

    return Object.values(this.updateCartIconActionForThemes).find((item) =>
      document.querySelector(item.selector)
    );
  }

  /**
   * Update cart count in theme-specific cart icon
   *
   * Exposes configuration to window and updates cart count using matched theme action
   * Automatically detects current theme and applies appropriate update logic
   *
   * @param cartItemCount - Number of items in cart
   * @example
   * ```javascript
   * cartSelectors.updateCartCount(3); // Updates cart icon to show 3 items
   * cartSelectors.updateCartCount(0); // May hide cart count bubble for some themes
   * ```
   */
  updateCartCount(cartItemCount: number): void {
    this.exposeConfig();
    // Find matching theme config and update cart count
    const matchedConfig = this.getCartSelectorAndUpdateAction();
    if (matchedConfig?.action) {
      matchedConfig.action(cartItemCount);
    }
  }
}
